// Renders the base "poster" — pure white background + deep indigo botanical
// silhouette illustration (tulips, daisies, poms, bellflowers, grass) plus
// butterfly and bee silhouettes. Flat solid color output; the CMYK halftone
// shader takes it from here. Typography lives in the DOM overlay, not here.

const INDIGO = '#241A5E';

type Species = 'tulip' | 'daisy' | 'pom' | 'bell';

interface FlowerSpec {
  species: Species;
  xRatio: number;
  heightRatio: number;
  tiltDeg: number;
}

const GARDEN: FlowerSpec[] = [
  { species: 'daisy', xRatio: 0.08, heightRatio: 0.30, tiltDeg: -6 },
  { species: 'tulip', xRatio: 0.15, heightRatio: 0.38, tiltDeg: 4 },
  { species: 'bell',  xRatio: 0.22, heightRatio: 0.34, tiltDeg: -3 },
  { species: 'pom',   xRatio: 0.30, heightRatio: 0.28, tiltDeg: 2 },
  { species: 'tulip', xRatio: 0.38, heightRatio: 0.42, tiltDeg: -5 },
  { species: 'daisy', xRatio: 0.46, heightRatio: 0.32, tiltDeg: 6 },
  { species: 'tulip', xRatio: 0.54, heightRatio: 0.40, tiltDeg: -2 },
  { species: 'bell',  xRatio: 0.62, heightRatio: 0.36, tiltDeg: 5 },
  { species: 'pom',   xRatio: 0.70, heightRatio: 0.30, tiltDeg: -4 },
  { species: 'daisy', xRatio: 0.78, heightRatio: 0.34, tiltDeg: 3 },
  { species: 'tulip', xRatio: 0.86, heightRatio: 0.38, tiltDeg: -6 },
  { species: 'bell',  xRatio: 0.93, heightRatio: 0.30, tiltDeg: 4 },
];

interface InsectSpec {
  kind: 'butterfly' | 'bee';
  xRatio: number;
  yRatio: number;      // 0..1 down from top
  sizeRatio: number;   // fraction of canvas height
  tiltDeg: number;
}

// Insects scattered above the garden strip.
const INSECTS: InsectSpec[] = [
  { kind: 'butterfly', xRatio: 0.19, yRatio: 0.58, sizeRatio: 0.08, tiltDeg: -12 },
  { kind: 'bee',       xRatio: 0.34, yRatio: 0.48, sizeRatio: 0.05, tiltDeg: 6 },
  { kind: 'butterfly', xRatio: 0.50, yRatio: 0.36, sizeRatio: 0.09, tiltDeg: 8 },
  { kind: 'bee',       xRatio: 0.58, yRatio: 0.62, sizeRatio: 0.045, tiltDeg: -10 },
  { kind: 'butterfly', xRatio: 0.72, yRatio: 0.50, sizeRatio: 0.075, tiltDeg: -6 },
  { kind: 'bee',       xRatio: 0.82, yRatio: 0.42, sizeRatio: 0.055, tiltDeg: 14 },
  { kind: 'butterfly', xRatio: 0.90, yRatio: 0.66, sizeRatio: 0.06, tiltDeg: 10 },
];

export function renderBaseArtwork(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): void {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = INDIGO;

  drawGarden(ctx, width, height);
  drawGrassBase(ctx, width, height);
  drawInsects(ctx, width, height);
}

// ---------------------------------------------------------------- garden
function drawGarden(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const baseY = height * 0.94;

  for (let i = 0; i < GARDEN.length; i += 1) {
    const spec = GARDEN[i];
    const cx = width * spec.xRatio;
    const totalH = height * spec.heightRatio;
    drawFlower(ctx, spec.species, cx, baseY, totalH, spec.tiltDeg);
  }
}

function drawFlower(
  ctx: CanvasRenderingContext2D,
  species: Species,
  cx: number,
  baseY: number,
  totalH: number,
  tiltDeg: number,
): void {
  const stemW = Math.max(totalH * 0.012, 2);

  ctx.save();
  ctx.translate(cx, baseY);
  ctx.rotate((tiltDeg * Math.PI) / 180);
  ctx.translate(-cx, -baseY);

  const flowerH = totalH * (species === 'tulip' ? 0.24 : species === 'daisy' ? 0.22 : species === 'pom' ? 0.28 : 0.30);
  const stemTopY = baseY - totalH + flowerH * 0.5;

  drawStem(ctx, cx, baseY, stemTopY, stemW, totalH);
  drawLeaves(ctx, species, cx, baseY, totalH);

  switch (species) {
    case 'tulip':
      drawTulipHead(ctx, cx, stemTopY, totalH * 0.16, flowerH);
      break;
    case 'daisy':
      drawDaisyHead(ctx, cx, stemTopY, flowerH * 0.55);
      break;
    case 'pom':
      drawPomHead(ctx, cx, stemTopY, flowerH * 0.5);
      break;
    case 'bell':
      drawBellHead(ctx, cx, stemTopY, totalH * 0.14, flowerH);
      break;
    default:
      break;
  }

  ctx.restore();
}

function drawStem(
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  topY: number,
  stemW: number,
  totalH: number,
): void {
  const sway = totalH * 0.05;
  ctx.beginPath();
  ctx.moveTo(cx - stemW, baseY);
  ctx.bezierCurveTo(
    cx - stemW - sway * 0.5, baseY - totalH * 0.35,
    cx + sway * 0.4, baseY - totalH * 0.7,
    cx - stemW * 0.4, topY,
  );
  ctx.lineTo(cx + stemW * 0.4, topY);
  ctx.bezierCurveTo(
    cx + sway * 0.4 + stemW * 0.8, baseY - totalH * 0.7,
    cx - stemW - sway * 0.5 + stemW * 2, baseY - totalH * 0.35,
    cx + stemW, baseY,
  );
  ctx.closePath();
  ctx.fill();
}

function drawLeaves(
  ctx: CanvasRenderingContext2D,
  species: Species,
  cx: number,
  baseY: number,
  totalH: number,
): void {
  if (species === 'tulip') {
    drawLeaf(ctx, cx - 2, baseY - totalH * 0.30, totalH * 0.22, totalH * 0.30, -34);
    drawLeaf(ctx, cx + 2, baseY - totalH * 0.50, totalH * 0.16, totalH * 0.22, 32);
  } else if (species === 'daisy') {
    drawLeaf(ctx, cx - 2, baseY - totalH * 0.28, totalH * 0.18, totalH * 0.22, -40);
    drawLeaf(ctx, cx + 2, baseY - totalH * 0.45, totalH * 0.14, totalH * 0.18, 42);
  } else if (species === 'pom') {
    drawLeaf(ctx, cx - 2, baseY - totalH * 0.25, totalH * 0.20, totalH * 0.24, -30);
  } else {
    drawLeaf(ctx, cx - 2, baseY - totalH * 0.32, totalH * 0.14, totalH * 0.18, -38);
    drawLeaf(ctx, cx + 2, baseY - totalH * 0.20, totalH * 0.12, totalH * 0.16, 40);
  }
}

function drawLeaf(
  ctx: CanvasRenderingContext2D,
  ax: number,
  ay: number,
  w: number,
  h: number,
  angleDeg: number,
): void {
  ctx.save();
  ctx.translate(ax, ay);
  ctx.rotate((angleDeg * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(w * 0.35, -h * 0.10, w * 0.75, -h * 0.55, w, -h);
  ctx.bezierCurveTo(w * 0.55, -h * 0.55, w * 0.30, -h * 0.20, 0, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------- flower heads
function drawTulipHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  bottomY: number,
  w: number,
  h: number,
): void {
  const hw = w / 2;
  ctx.beginPath();
  ctx.moveTo(cx - hw, bottomY);
  ctx.bezierCurveTo(
    cx - hw * 1.18, bottomY - h * 0.35,
    cx - hw * 0.95, bottomY - h * 0.98,
    cx - hw * 0.55, bottomY - h * 1.0,
  );
  ctx.bezierCurveTo(
    cx - hw * 0.30, bottomY - h * 0.72,
    cx - hw * 0.24, bottomY - h * 0.70,
    cx - hw * 0.11, bottomY - h * 0.97,
  );
  ctx.bezierCurveTo(
    cx - hw * 0.04, bottomY - h * 1.08,
    cx + hw * 0.04, bottomY - h * 1.08,
    cx + hw * 0.11, bottomY - h * 0.97,
  );
  ctx.bezierCurveTo(
    cx + hw * 0.24, bottomY - h * 0.70,
    cx + hw * 0.30, bottomY - h * 0.72,
    cx + hw * 0.55, bottomY - h * 1.0,
  );
  ctx.bezierCurveTo(
    cx + hw * 0.95, bottomY - h * 0.98,
    cx + hw * 1.18, bottomY - h * 0.35,
    cx + hw, bottomY,
  );
  ctx.bezierCurveTo(
    cx + hw * 0.5, bottomY + h * 0.10,
    cx - hw * 0.5, bottomY + h * 0.10,
    cx - hw, bottomY,
  );
  ctx.closePath();
  ctx.fill();
}

function drawDaisyHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  centerY: number,
  radius: number,
): void {
  const petals = 8;
  const petalLen = radius * 1.1;
  const petalW = radius * 0.42;
  for (let i = 0; i < petals; i += 1) {
    const a = (i / petals) * Math.PI * 2;
    ctx.save();
    ctx.translate(cx, centerY - radius * 0.05);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.ellipse(0, -petalLen * 0.55, petalW * 0.5, petalLen * 0.55, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.beginPath();
  ctx.arc(cx, centerY - radius * 0.05, radius * 0.38, 0, Math.PI * 2);
  ctx.fill();
}

function drawPomHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  centerY: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.arc(cx, centerY - radius * 0.1, radius, 0, Math.PI * 2);
  ctx.fill();

  const bumps = 10;
  for (let i = 0; i < bumps; i += 1) {
    const a = (i / bumps) * Math.PI * 2;
    const bx = cx + Math.cos(a) * radius * 0.92;
    const by = centerY - radius * 0.1 + Math.sin(a) * radius * 0.92;
    const br = radius * (0.22 + ((i * 37) % 100) / 400);
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawBellHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  bottomY: number,
  w: number,
  h: number,
): void {
  const bells = 3;
  for (let i = 0; i < bells; i += 1) {
    const by = bottomY - (h * 0.28) * i - h * 0.05;
    const bw = w * (1 - i * 0.12);
    const bh = h * 0.28;
    drawBell(ctx, cx + (i % 2 === 0 ? -w * 0.05 : w * 0.05), by, bw, bh);
  }
}

function drawBell(
  ctx: CanvasRenderingContext2D,
  cx: number,
  bottomY: number,
  w: number,
  h: number,
): void {
  const hw = w / 2;
  ctx.beginPath();
  ctx.moveTo(cx - hw * 0.5, bottomY - h);
  ctx.bezierCurveTo(
    cx - hw * 1.1, bottomY - h * 0.6,
    cx - hw * 1.15, bottomY - h * 0.1,
    cx - hw * 0.7, bottomY,
  );
  ctx.quadraticCurveTo(cx, bottomY + h * 0.08, cx + hw * 0.7, bottomY);
  ctx.bezierCurveTo(
    cx + hw * 1.15, bottomY - h * 0.1,
    cx + hw * 1.1, bottomY - h * 0.6,
    cx + hw * 0.5, bottomY - h,
  );
  ctx.quadraticCurveTo(cx, bottomY - h * 0.9, cx - hw * 0.5, bottomY - h);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, bottomY + h * 0.05, w * 0.06, 0, Math.PI * 2);
  ctx.fill();
}

// ---------------------------------------------------------------- insects
function drawInsects(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  for (let i = 0; i < INSECTS.length; i += 1) {
    const spec = INSECTS[i];
    const cx = width * spec.xRatio;
    const cy = height * spec.yRatio;
    const size = height * spec.sizeRatio;
    if (spec.kind === 'butterfly') {
      drawButterfly(ctx, cx, cy, size, spec.tiltDeg);
    } else {
      drawBee(ctx, cx, cy, size, spec.tiltDeg);
    }
  }
}

function drawButterfly(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tiltDeg: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((tiltDeg * Math.PI) / 180);

  // Body — slim ellipse
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.06, size * 0.42, 0, 0, Math.PI * 2);
  ctx.fill();

  // Head
  ctx.beginPath();
  ctx.arc(0, -size * 0.42, size * 0.08, 0, Math.PI * 2);
  ctx.fill();

  // Antennae
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.48);
  ctx.quadraticCurveTo(-size * 0.15, -size * 0.65, -size * 0.22, -size * 0.72);
  ctx.moveTo(0, -size * 0.48);
  ctx.quadraticCurveTo( size * 0.15, -size * 0.65,  size * 0.22, -size * 0.72);
  ctx.lineWidth = Math.max(size * 0.02, 1);
  ctx.strokeStyle = INDIGO;
  ctx.stroke();

  // Wings — upper (larger) and lower (smaller), mirrored
  drawButterflyWing(ctx, -1, size);
  drawButterflyWing(ctx,  1, size);

  ctx.restore();
}

function drawButterflyWing(
  ctx: CanvasRenderingContext2D,
  side: -1 | 1,
  size: number,
): void {
  // Upper wing — big rounded triangle
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.20);
  ctx.bezierCurveTo(
    side * size * 0.15, -size * 0.55,
    side * size * 0.70, -size * 0.55,
    side * size * 0.80, -size * 0.15,
  );
  ctx.bezierCurveTo(
    side * size * 0.75,  size * 0.05,
    side * size * 0.35,  size * 0.05,
    0, -size * 0.05,
  );
  ctx.closePath();
  ctx.fill();

  // Lower wing — smaller lobed shape
  ctx.beginPath();
  ctx.moveTo(0, -size * 0.02);
  ctx.bezierCurveTo(
    side * size * 0.35,  size * 0.05,
    side * size * 0.60,  size * 0.30,
    side * size * 0.50,  size * 0.48,
  );
  ctx.bezierCurveTo(
    side * size * 0.35,  size * 0.55,
    side * size * 0.10,  size * 0.35,
    0,  size * 0.22,
  );
  ctx.closePath();
  ctx.fill();

  // Small wing decoration dot (still solid indigo — the shader will do the CMYK)
  ctx.beginPath();
  ctx.arc(side * size * 0.42, -size * 0.28, size * 0.06, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.fillStyle = INDIGO;
}

function drawBee(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tiltDeg: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((tiltDeg * Math.PI) / 180);

  // Body — chubby oval
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.55, size * 0.38, 0, 0, Math.PI * 2);
  ctx.fill();

  // Head — small circle in front
  ctx.beginPath();
  ctx.arc(-size * 0.55, -size * 0.05, size * 0.22, 0, Math.PI * 2);
  ctx.fill();

  // Stinger tail
  ctx.beginPath();
  ctx.moveTo(size * 0.55, 0);
  ctx.lineTo(size * 0.85, -size * 0.08);
  ctx.lineTo(size * 0.85,  size * 0.08);
  ctx.closePath();
  ctx.fill();

  // Belly stripes carved out with white — the shader will turn them into
  // halftone gaps, which reads unmistakably as a bee.
  ctx.fillStyle = '#FFFFFF';
  for (let i = -1; i <= 1; i += 1) {
    ctx.beginPath();
    ctx.ellipse(size * 0.10 * i, 0, size * 0.06, size * 0.32, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = INDIGO;

  // Wings — two translucent-looking ovals above the body (solid indigo,
  // shader will halftone them into a nice gauzy texture)
  ctx.beginPath();
  ctx.ellipse(-size * 0.10, -size * 0.42, size * 0.30, size * 0.18, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse( size * 0.15, -size * 0.42, size * 0.28, size * 0.16, 0.4, 0, Math.PI * 2);
  ctx.fill();

  // Antennae
  ctx.lineWidth = Math.max(size * 0.03, 1);
  ctx.strokeStyle = INDIGO;
  ctx.beginPath();
  ctx.moveTo(-size * 0.68, -size * 0.20);
  ctx.quadraticCurveTo(-size * 0.85, -size * 0.45, -size * 0.90, -size * 0.55);
  ctx.moveTo(-size * 0.60, -size * 0.22);
  ctx.quadraticCurveTo(-size * 0.70, -size * 0.45, -size * 0.75, -size * 0.55);
  ctx.stroke();

  ctx.restore();
}

// ---------------------------------------------------------------- grass base
function drawGrassBase(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): void {
  const baseY = height * 0.945;
  const rng = mulberry32(913);

  // Step 1: guaranteed solid slab from baseY down to the very bottom edge.
  // This eliminates any chance of a horizontal seam appearing between the
  // grass blades (which root at baseY) and the halftone base band.
  ctx.fillRect(0, baseY, width, height - baseY);

  // Step 2: organic wobbly top edge that only grows UPWARD from baseY, so
  // the solid region is always at least y >= baseY. Never dips below baseY.
  ctx.beginPath();
  ctx.moveTo(0, baseY);
  const seg = 96;
  for (let i = 0; i <= seg; i += 1) {
    const x = (width * i) / seg;
    // Negative-only offset: shape rises above baseY, never drops below it.
    const wobble = -Math.abs(rng() - 0.5) * height * 0.055;
    const wave = -Math.abs(Math.sin(i * 0.45)) * height * 0.012;
    ctx.lineTo(x, baseY + wave + wobble);
  }
  ctx.lineTo(width, baseY);
  ctx.closePath();
  ctx.fill();

  const dotRng = mulberry32(4211);
  const dotCount = Math.round((width * height) / 22000);
  for (let i = 0; i < dotCount; i += 1) {
    const x = dotRng() * width;
    const y = baseY - dotRng() * height * 0.10;
    const r = dotRng() * height * 0.006 + height * 0.0012;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  const bladeRng = mulberry32(77);
  const bladeCount = Math.round(width / 22);
  for (let i = 0; i < bladeCount; i += 1) {
    const x = bladeRng() * width;
    const grassH = height * 0.025 + bladeRng() * height * 0.05;
    const bladeW = height * 0.003 + bladeRng() * height * 0.002;
    const tipX = x + (bladeRng() - 0.5) * height * 0.03;
    ctx.beginPath();
    ctx.moveTo(x - bladeW, baseY);
    ctx.quadraticCurveTo(x + (bladeRng() - 0.5) * height * 0.02, baseY - grassH * 0.7, tipX, baseY - grassH);
    ctx.quadraticCurveTo(x + (bladeRng() - 0.5) * height * 0.02 + bladeW, baseY - grassH * 0.7, x + bladeW, baseY);
    ctx.closePath();
    ctx.fill();
  }
}

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return function generate(): number {
    t = (t + 0x6d2b79f5) | 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}